// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
import React from 'react';
import {
  ComposedChart,
  AreaChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { MonthlyAcquiredBookData } from '../../hooks/useAcquiredBookMetrics';

interface RetentionTrendChartProps {
  data: MonthlyAcquiredBookData[];
  title?: string;
  viewMode?: 'combined' | 'simple';
}

/**
 * Retention Trend Chart - Google Enterprise Style
 *
 * Two view modes:
 * - Combined (Detailed): Stacked bars + retention rate line
 * - Simple (Stacked): "Opportunity" area chart showing total vs captured
 */
const RetentionTrendChart: React.FC<RetentionTrendChartProps> = ({
  data,
  title = 'Monthly Retention Trend',
  viewMode = 'combined',
}) => {
  // Format data for chart - ensure sorted by month
  const chartData = [...data].sort((a, b) => a.month.localeCompare(b.month));

  // Format month label for X-axis
  const formatMonth = (value: string) => {
    const [year, month] = value.split('-');
    return `${year}-${month}`;
  };

  // Format month for tooltip
  const formatMonthFull = (value: string) => {
    const [year, month] = value.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Custom tooltip for detailed view
  const DetailedTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const retained = payload.find((p: any) => p.dataKey === 'retained')?.value || 0;
      const lost = payload.find((p: any) => p.dataKey === 'lost')?.value || 0;
      const rate = payload.find((p: any) => p.dataKey === 'retentionRate')?.value || 0;
      const discharges = retained + lost;

      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
          <p className="font-bold mb-2 text-gray-300">{formatMonthFull(label)}</p>
          <div className="space-y-1 text-sm">
            <p className="text-gray-400">
              Total Discharged: <span className="font-medium text-white">{discharges.toLocaleString()}</span>
            </p>
            <div className="border-t border-gray-700 my-2" />
            <p className="text-green-400">
              Retained: <span className="font-medium">{retained.toLocaleString()}</span>
            </p>
            <p className="text-gray-400">
              Lost: <span className="font-medium text-gray-300">{lost.toLocaleString()}</span>
            </p>
            <div className="border-t border-gray-700 my-2" />
            <p className="text-blue-400 font-medium">
              Retention Rate: {rate}%
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for opportunity area chart
  const OpportunityTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const discharges = payload.find((p: any) => p.dataKey === 'discharges')?.value || 0;
      const retained = payload.find((p: any) => p.dataKey === 'retained')?.value || 0;
      const rate = discharges > 0 ? Math.round((retained / discharges) * 100) : 0;

      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
          <p className="font-bold mb-2 text-gray-300">{formatMonthFull(label)}</p>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#374151' }} />
              <span className="text-gray-400">Total Opportunity:</span>
              <span className="font-medium text-white">{discharges}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500" />
              <span className="text-gray-400">Retained:</span>
              <span className="font-medium text-emerald-400">{retained}</span>
            </div>
            <div className="border-t border-gray-700 my-2" />
            <p className="text-center">
              <span className="text-2xl font-bold text-emerald-400">{rate}%</span>
              <span className="text-gray-500 ml-1 text-xs">captured</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
        <h3 className="font-bold text-lg mb-4 text-white">{title}</h3>
        <div className="h-64 flex items-center justify-center text-gray-400">
          No discharge data available for this period
        </div>
      </div>
    );
  }

  // Custom label renderer for retention rate on area chart
  const renderRetentionLabel = (props: any) => {
    const { x, y, index, value } = props;
    const dataPoint = chartData[index];
    if (!dataPoint) return null;

    // Only show labels for points with significant retention (>10%) and spread them out
    // Show every 4th point, or if retention rate is notably high
    const showLabel = dataPoint.retentionRate >= 10 && (
      index % 4 === 0 ||
      dataPoint.retentionRate >= 20
    );

    if (!showLabel) return null;

    return (
      <text
        x={x}
        y={y - 10}
        fill="#10B981"
        fontSize={11}
        fontWeight="bold"
        textAnchor="middle"
      >
        {dataPoint.retentionRate}%
      </text>
    );
  };

  // Opportunity Area Chart - Total vs Captured visualization
  const renderSimpleView = () => (
    <ResponsiveContainer width="100%" height={320}>
      <AreaChart
        data={chartData}
        margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
      >
        <defs>
          <linearGradient id="opportunityGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#374151" stopOpacity={0.9} />
            <stop offset="95%" stopColor="#374151" stopOpacity={0.3} />
          </linearGradient>
          <linearGradient id="retainedGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10B981" stopOpacity={0.9} />
            <stop offset="95%" stopColor="#10B981" stopOpacity={0.4} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" opacity={0.5} />
        <XAxis
          dataKey="month"
          stroke="#6B7280"
          fontSize={11}
          tickFormatter={formatMonth}
          tick={{ fill: '#9CA3AF' }}
        />
        <YAxis
          stroke="#6B7280"
          fontSize={11}
          tick={{ fill: '#9CA3AF' }}
          label={{
            value: 'Number of Loans',
            angle: -90,
            position: 'insideLeft',
            fill: '#9CA3AF',
            fontSize: 11,
            offset: 0,
          }}
        />
        <Tooltip content={<OpportunityTooltip />} />
        <Legend
          verticalAlign="top"
          align="right"
          wrapperStyle={{ paddingBottom: 10 }}
          formatter={(value) => {
            const labels: Record<string, string> = {
              discharges: 'Total Opportunity (Discharged)',
              retained: 'Retained (Captured)',
            };
            return <span className="text-xs text-gray-400">{labels[value] || value}</span>;
          }}
        />

        {/* Background "Mountain" - Total Opportunity */}
        <Area
          type="linear"
          dataKey="discharges"
          stroke="#4B5563"
          strokeWidth={2}
          fill="url(#opportunityGradient)"
          name="discharges"
        />

        {/* Foreground - Retained/Captured */}
        <Area
          type="linear"
          dataKey="retained"
          stroke="#10B981"
          strokeWidth={2}
          fill="url(#retainedGradient)"
          name="retained"
        >
          <LabelList
            dataKey="retained"
            content={renderRetentionLabel}
          />
        </Area>
      </AreaChart>
    </ResponsiveContainer>
  );

  // Combined Bar + Line View - Detailed dual-axis visualization
  const renderCombinedView = () => (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart
        data={chartData}
        margin={{ top: 20, right: 60, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#344155" opacity={0.3} />
        <XAxis
          dataKey="month"
          stroke="#8892b0"
          fontSize={12}
          tickFormatter={formatMonth}
        />
        <YAxis
          yAxisId="left"
          stroke="#8892b0"
          fontSize={12}
          label={{
            value: 'Loans',
            angle: -90,
            position: 'insideLeft',
            fill: '#8892b0',
            fontSize: 11,
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          stroke="#60A5FA"
          fontSize={12}
          domain={[0, 100]}
          tickFormatter={(value) => `${value}%`}
          label={{
            value: 'Retention %',
            angle: 90,
            position: 'insideRight',
            fill: '#60A5FA',
            fontSize: 11,
          }}
        />
        <Tooltip content={<DetailedTooltip />} />
        <Legend
          wrapperStyle={{ paddingTop: 10 }}
          formatter={(value) => {
            const labels: Record<string, string> = {
              retained: 'Retained to Primary',
              lost: 'Lost to External',
              retentionRate: 'Retention Rate',
            };
            return labels[value] || value;
          }}
        />

        {/* Stacked bars showing retained + lost = total discharged */}
        <Bar
          yAxisId="left"
          dataKey="retained"
          stackId="a"
          fill="#10B981"
          name="retained"
          radius={[0, 0, 0, 0]}
        />
        <Bar
          yAxisId="left"
          dataKey="lost"
          stackId="a"
          fill="#6B7280"
          name="lost"
          radius={[4, 4, 0, 0]}
        />

        {/* Retention rate line */}
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="retentionRate"
          stroke="#60A5FA"
          strokeWidth={3}
          dot={{ fill: '#60A5FA', r: 4 }}
          activeDot={{ r: 6 }}
          name="retentionRate"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10"
        style={{ background: 'radial-gradient(circle at 50% 0%, #10B98140 0%, transparent 70%)' }}
      />

      {viewMode === 'simple' ? renderSimpleView() : renderCombinedView()}

      {/* Legend explanation */}
      <div className="mt-4 flex items-center justify-center gap-6 text-xs text-gray-400">
        {viewMode === 'combined' ? (
          <>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-green-500" />
              <span>Retained (confirmed refinances into primary)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-gray-500" />
              <span>Lost (not retained)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-blue-400" />
              <span>Retention Rate %</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#374151' }} />
              <span>Total Opportunity (all discharged)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500" />
              <span>Captured (confirmed refinances into primary)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-emerald-400 font-medium">%</span>
              <span>Retention Rate labels</span>
            </div>
          </>
        )}
      </div>

      {/* WS1: "retained" now counts confirmed refinances only */}
      <p className="mt-3 text-center text-[11px] text-gray-500">
        Retained = confirmed refinances from the acquired book into the primary book.
      </p>
    </div>
  );
};

export default RetentionTrendChart;
